import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import './Navigation.css';
import logoCaes from '../assets/images/logo-nav.png';


const navLinks = [
  { title: 'Inicio', path: '/' },
  {
    title: 'Institucional',
    submenu: [
      { title: 'Quiénes Somos', path: '/institucional#quienes-somos' },
      { title: 'Historia', path: '/institucional#historia' },
    ],
  },
  {
    title: 'Congreso',
    submenu: [
      { title: 'Congreso', path: '/congreso' },
      { title: 'Historia del Congreso', path: '/congreso#historia-congreso' },
    ],
  },
  { title: 'Recursos', submenu: [
      { title: 'Biblioteca Digital', path: '/Biblioteca' },
      { title: 'Estudios de Mercado', path: '/estudios-de-mercado' },
    ], },


  
  { title: 'Noticias', path: '/noticias' },
  { title: 'Contacto', path: '/contacto' },
  { title: 'Asóciese', path: '/institucional#asociese', isCta: true },
];

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const navRef = useRef(null);
  useClickOutside(navRef, () => setOpenSubmenu(null));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => {
    setMenuOpen(false);
    setOpenSubmenu(null);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      setMenuOpen(true);
    }
  };

  const mobileLinkVariants = {
    initial: { opacity: 0, y: 15 },
    open: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    closed: { opacity: 0, y: 15, transition: { duration: 0.2 } }
  };
  
  const renderNavLinks = (isMobile = false) => {
    return navLinks.map((link, index) => {
      if (isMobile) {
        if (link.isCta) {
          return (
            <motion.li key={link.title} variants={mobileLinkVariants} className="cta-item">
               <div className="separator"></div>
               <Link to={link.path} className='btn-cta-mobile' onClick={closeMenu}>
                {link.title}
              </Link>
            </motion.li>
          )
        }
        return (
          <motion.li key={link.title} variants={mobileLinkVariants}>
            {link.submenu ? (
               <div className="mobile-submenu-container">
                <span onClick={() => setOpenSubmenu(openSubmenu === index ? null : index)}>
                  {link.title} <FaChevronDown size="0.8em" />
                </span>
                <AnimatePresence>
                  {openSubmenu === index && (
                    <motion.div 
                      className="mobile-submenu"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      {link.submenu.map(sublink => (
                        <Link key={sublink.title} to={sublink.path} onClick={closeMenu}>
                          {sublink.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to={link.path} onClick={closeMenu}>
                {link.title}
              </Link>
            )}
          </motion.li>
        );
      }

      // Escritorio
      if (link.submenu) {
        return (
            <li key={link.title} className="nav-item has-submenu" onMouseEnter={() => setOpenSubmenu(index)} onMouseLeave={() => setOpenSubmenu(null)}>
                <span className="submenu-trigger">{link.title}<FaChevronDown size="0.8em" /></span>
                <AnimatePresence>
                {openSubmenu === index && (
                    <motion.div className="submenu" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 15 }}>
                    {link.submenu.map(sublink => (<Link key={sublink.title} to={sublink.path}>{sublink.title}</Link>))}
                    </motion.div>
                )}
                </AnimatePresence>
            </li>
        );
      }
      return (
        <li key={link.title} className="nav-item">
          <NavLink to={link.path} end={link.path === '/'} className={link.isCta ? 'btn-cta' : ''}>{link.title}</NavLink>
        </li>
      );
    });
  };

  return (
    <header>
      <nav className={`navbar-future ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to="/" className="logo"><img src={logoCaes} alt="Logo de CAES" /></Link>
          <ul className="nav-links-desktop">{renderNavLinks()}</ul>
          <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={toggleMenu} aria-label="Abrir menú">
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
          >
            <div className="mobile-menu-header">
                <Link to="/" className="logo-mobile" onClick={closeMenu}>
                    <img src={logoCaes} alt="Logo de CAES" />
                </Link>
                <button className={`hamburger open`} onClick={closeMenu} aria-label="Cerrar menú">
                    <span></span><span></span><span></span>
                </button>
            </div>

            <motion.ul 
              className="mobile-menu-links"
              initial="initial"
              animate="open"
              exit="closed"
              variants={{ open: { transition: { staggerChildren: 0.05, delayChildren: 0.15 } } }}
            >
              {renderNavLinks(true)}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Navigation;